import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { apiRequest } from '@/src/services';
import { useDictionary, usePage } from '@/src/app/hooks';
import { Loading, ErrorMessage, Container, type UnitDTO, type ResourceHeaderDTO } from '@/src/ui';
import {
    filterUnitsData,
    filterAssessmentsData,
    filterResourcesData,
    filterLanguageSetting,
} from '@/src/utils';
import './AllLessonsPage.css';
import CollapsibleUnit from './components/CollapsibleUnit';

const AllLessonsPage: React.FC = () => {
    const { translate } = useDictionary();
    const { setSubheaderTitle, setSubheaderType, pageGrade, setPageGrade, pageLanguage } =
        usePage();
    document.title = translate('All Lessons', 'All Lessons');

    const params = useParams();
    const grade = params.grade || pageGrade;
    useEffect(() => {
        if (grade !== undefined && grade !== pageGrade) {
            setPageGrade(grade);
        }
    }, [grade, pageGrade, setPageGrade]);

    const [unitsData, setUnitsData] = useState<UnitDTO[] | null>(null);
    const [assessmentsData, setAssessmentsData] = useState<ResourceHeaderDTO[] | null>(null);
    const [resourcesData, setResourcesData] = useState<ResourceHeaderDTO[] | null>(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const [selectedUnit, setSelectedUnit] = useState<number | null>(
        params.unit ? parseInt(params.unit) : null
    );
    const [selectedLesson, setSelectedLesson] = useState<number | null>(
        params.lesson ? parseInt(params.lesson) : null
    );
    const [selectedTab, setSelectedTab] = useState<'lessons' | 'assessments' | 'resources' | null>(
        null
    );

    useEffect(() => {
        setSubheaderTitle(translate('Grade') + ' ' + grade);
        setSubheaderType('hero');
        setPageGrade(grade);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [translate, grade]);

    // Allows grade to load from dashboard MyLessons without clearing selected values
    const loadedGrade = useRef(grade);
    useEffect(() => {
        if (loadedGrade.current == grade) {
            return;
        }
        loadedGrade.current = grade;
        setSelectedUnit(null);
        setSelectedLesson(null);
        setSelectedTab(null);
    }, [grade]);

    useEffect(() => {
        (async function fetchData() {
            setError('');
            setLoading(true);
            setUnitsData(null);
            setAssessmentsData(null);
            setResourcesData(null);
            try {
                const unitsRes = await apiRequest<UnitDTO[]>(
                    'GET',
                    `dbo/lessons?grade=${grade}&language=${filterLanguageSetting(grade, pageLanguage)}`
                );
                setUnitsData(filterUnitsData(grade, unitsRes.data, pageLanguage));

                const assessmentsRes = await apiRequest<ResourceHeaderDTO[]>(
                    'GET',
                    `dbo/assessments?grade=${grade}&language=${filterLanguageSetting(grade, pageLanguage)}`
                );
                setAssessmentsData(filterAssessmentsData(assessmentsRes.data, grade, pageLanguage));

                const resourcesRes = await apiRequest<ResourceHeaderDTO[]>(
                    'GET',
                    `dbo/resources?grade=${grade}&language=${filterLanguageSetting(grade, pageLanguage)}`
                );
                setResourcesData(filterResourcesData(resourcesRes.data, grade, pageLanguage));
            } catch (e: any) {
                if (e.status === 403) {
                    setError('You do not have permission to view this grade.');
                } else if (e.status === 404) {
                    setError('Data could not be found at this time.');
                } else {
                    setError('Failed to fetch data');
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [grade, pageLanguage]);

    return (
        <Container>
            <ErrorMessage error={error} />
            {loading ? (
                <Loading />
            ) : (
                <div id="unitsContainer">
                    {unitsData
                        ? unitsData.map((unit: UnitDTO, index: number) => {
                              const key = `allLessons-unit-${grade}-${index}`;
                              return (
                                  <CollapsibleUnit
                                      key={key}
                                      loading={loading}
                                      error={error}
                                      assessmentsData={assessmentsData}
                                      resourcesData={resourcesData}
                                      unit={unit}
                                      index={index}
                                      selectedUnit={selectedUnit}
                                      selectedLesson={selectedLesson}
                                      selectedTab={selectedTab}
                                      setSelectedUnit={setSelectedUnit}
                                      setSelectedTab={setSelectedTab}
                                      setSelectedLesson={setSelectedLesson}
                                  />
                              );
                          })
                        : null}
                </div>
            )}
        </Container>
    );
};

export default AllLessonsPage;
